import { useState, useEffect } from 'react'
import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { useTheme } from '@/components/theme-provider'
import { LoginDialog } from '@/components/login-dialog'
import { PasscodeDialog } from '@/components/passcode-dialog'
import authService from '@/lib/auth'
import logoImage from '@/assets/breakthrough-health-logo.jpeg'
import {
  Heart,
  Users,
  Activity,
  TrendingUp,
  Shield,
  CheckCircle2,
  AlertCircle,
  Sun,
  Moon,
  Stethoscope,
  ClipboardCheck,
  Calendar,
  BarChart3
} from 'lucide-react'

interface LandingProps {
  return_to?: string
}

const NAV_LINKS = [
  { label: 'What is Hep C', id: 'what-is-hepc' },
  { label: 'Global Impact', id: 'global-impact' },
  { label: 'About Us', id: 'about-us' },
  { label: 'Treatment Journey', id: 'treatment-journey' },
  { label: 'Our Platform', id: 'our-platform' },
  { label: 'Access Platform', id: 'access-platform' },
]

export default function Landing({ return_to }: LandingProps) {
  const { theme, setTheme } = useTheme()
  const [passcodeDialogOpen, setPasscodeDialogOpen] = useState(false)
  const [loginDialogOpen, setLoginDialogOpen] = useState(false)
  const [isCheckingAuth, setIsCheckingAuth] = useState(true)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id)
    if (element) {
      const offset = 88
      const top = element.getBoundingClientRect().top + window.scrollY - offset
      window.scrollTo({ top, behavior: 'smooth' })
    }
    setMobileMenuOpen(false)
  }

  // Check if user is already authenticated (token in localStorage)
  useEffect(() => {
    const checkExistingAuth = async () => {
      const token = authService.getToken()
      const refreshToken = authService.getRefreshToken()

      // No token at all - show landing page
      if (!token) {
        setIsCheckingAuth(false)
        return
      }

      // Token expired but refresh token exists - try to refresh
      if (authService.isTokenExpired() && refreshToken) {
        const refreshed = await authService.refreshJwtToken()
        if (refreshed) {
          // Refresh succeeded, redirect to dashboard (Inertia XHR will include JWT)
          router.visit(return_to || '/', { replace: true })
          return
        } else {
          // Refresh failed, clear tokens and show landing page
          authService.clearTokens()
          setIsCheckingAuth(false)
          return
        }
      }

      // Token exists and is not expired - redirect to dashboard
      if (!authService.isTokenExpired()) {
        router.visit(return_to || '/', { replace: true })
      } else {
        // Token expired and no refresh token - show landing page
        authService.clearTokens()
        setIsCheckingAuth(false)
      }
    }

    checkExistingAuth()
  }, [return_to])

  const handleLoginSuccess = () => {
    setLoginDialogOpen(false)
    const redirectTo = return_to || '/'
    router.visit(redirectTo, { replace: true })
  }

  const handlePasscodeSuccess = () => {
    setPasscodeDialogOpen(false)
    setLoginDialogOpen(true)
  }

  const handleAccessClick = () => {
    setPasscodeDialogOpen(true)
  }

  // Show loading state while checking authentication
  if (isCheckingAuth) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-teal-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b bg-white/80 dark:bg-slate-900/80 backdrop-blur-md">
        <div className="container mx-auto flex h-20 items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <img
              src={logoImage}
              alt="Breakthrough Health Logo"
              className="h-14 w-auto rounded-lg shadow-sm"
            />
            <div className="flex flex-col">
              <span className="text-lg font-bold text-slate-900 dark:text-white">Breakthrough Health, Inc.</span>
              <span className="text-xs text-slate-600 dark:text-slate-400">Hepatitis C Treatment Platform</span>
            </div>
          </div>

          {/* Desktop nav links */}
          <div className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="px-3 py-1.5 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/40 rounded-lg transition-all"
              >
                {link.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              onClick={() => setMobileMenuOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              <svg className="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen
                  ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                }
              </svg>
            </button>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            >
              <Sun className="size-5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
              <Moon className="absolute size-5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
            </Button>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md px-4 py-3 flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="text-left px-3 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/40 rounded-lg transition-all"
              >
                {link.label}
              </button>
            ))}
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <main className="container mx-auto px-4 pt-32 pb-16">
        <div className="flex flex-col items-center text-center">
          {/* Large Logo */}
          <div className="mb-8 animate-fade-in">
            <img
              src={logoImage}
              alt="Breakthrough Health Logo"
              className="h-40 w-auto md:h-52 lg:h-64 rounded-2xl shadow-2xl ring-4 ring-blue-100 dark:ring-blue-900/50 animate-float"
            />
          </div>

          {/* Hero Badge */}
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/50 px-5 py-2 text-sm font-medium animate-fade-in">
            <Heart className="size-4 text-blue-600 dark:text-blue-400" />
            <span className="text-blue-700 dark:text-blue-300">Together, we can end Hepatitis C</span>
          </div>

          {/* Main Headline */}
          <h1 className="mb-6 max-w-4xl text-4xl font-bold leading-tight tracking-tight text-slate-900 dark:text-white md:text-5xl lg:text-6xl">
            Understanding{' '}
            <span className="bg-gradient-to-r from-blue-600 via-teal-600 to-blue-600 bg-clip-text text-transparent animate-gradient">
              Hepatitis C
            </span>
          </h1>

          {/* Subheadline */}
          <p className="mb-8 max-w-2xl text-lg text-slate-600 dark:text-slate-300 md:text-xl">
            A chronic viral infection affecting millions worldwide. With proper treatment and care,
            Hepatitis C can be cured in most cases.
          </p>
        </div>

        {/* What is Hepatitis C Section */}
        <div id="what-is-hepc" className="mt-20 max-w-6xl mx-auto">
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8 md:p-12 animate-scale-in">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex size-12 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <AlertCircle className="size-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white">What is Hepatitis C?</h2>
            </div>
            <div className="prose prose-slate dark:prose-invert max-w-none">
              <p className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
                Hepatitis C is a viral infection that causes liver inflammation. The virus is spread through
                contact with contaminated blood. Many people with Hepatitis C don't know they have it because
                they may not have symptoms for years, even decades.
              </p>
              <p className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed mt-4">
                If left untreated, chronic Hepatitis C can lead to serious liver problems including cirrhosis
                (scarring of the liver), liver failure, and liver cancer. However, with today's treatments,
                most people can be cured.
              </p>
            </div>
          </div>
        </div>

        {/* Impact Statistics */}
        <div id="global-impact" className="mt-16 max-w-6xl mx-auto">
          <h2 className="mb-8 text-3xl font-bold text-center text-slate-900 dark:text-white animate-fade-in">The Global Impact</h2>
          <div className="grid gap-6 md:grid-cols-3 stagger-animation">
            {[
              {
                icon: Users,
                stat: '58 Million',
                label: 'People living with chronic Hepatitis C worldwide',
                color: 'from-blue-500 to-blue-600',
              },
              {
                icon: TrendingUp,
                stat: '95%+',
                label: 'Cure rate with modern direct-acting antiviral treatments',
                color: 'from-teal-500 to-teal-600',
              },
              {
                icon: Activity,
                stat: '290,000',
                label: 'Annual deaths from Hepatitis C complications globally',
                color: 'from-red-500 to-red-600',
              },
            ].map((item) => (
              <div
                key={item.label}
                className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 transition-all duration-300 hover:border-slate-300 dark:hover:border-slate-700 hover:shadow-xl hover:-translate-y-1"
              >
                <div className={`mb-4 inline-flex size-12 items-center justify-center rounded-lg bg-gradient-to-br ${item.color}`}>
                  <item.icon className="size-6 text-white" />
                </div>
                <div className="text-3xl font-bold text-slate-900 dark:text-white mb-2">{item.stat}</div>
                <p className="text-slate-600 dark:text-slate-400">{item.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* About Us Section */}
        <div id="about-us" className="mt-20 max-w-6xl mx-auto">
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-gradient-to-br from-slate-50 via-white to-blue-50 dark:from-slate-900 dark:via-slate-800 dark:to-blue-950/30 p-8 md:p-12 shadow-xl animate-slide-in-up">
            <div className="flex items-center gap-3 mb-8">
              <div className="flex size-12 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-teal-600">
                <Heart className="size-6 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white">About Us</h2>
            </div>
            <div className="prose prose-lg prose-slate dark:prose-invert max-w-none">
              <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                It's a strange irony of modern medicine: we possess the power to cure, but not the systems to deliver it. In an age where artificial intelligence can compose symphonies and forecast weather with staggering precision, we still let hundreds of thousands die from a disease we know how to cure. Why though? Hepatitis C is not an unsolvable riddle. It's not a medical mystery. It is a curable disease, trapped inside an incurable system failure.
              </p>
              <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4 font-semibold">
                But that failure is not inevitable.
              </p>
              <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                We believe the true test of innovation isn't whether we can invent new miracles, it's whether we can bring known ones to those who need them most. Curing Hepatitis C does not require new science. It requires new infrastructure. It requires the courage to reach into the broken seams of our public health system and stitch together a path from diagnosis to cure, especially for those who have been ignored, overlooked, or forgotten.
              </p>
              <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                We know who they are: the man recently released from incarceration with no primary care. The woman struggling with substance use, cycling in and out of shelters. The child of immigrants who never gets tested because no one ever asks. These are not edge cases. They are the very center of the crisis. And they are the reason we are building Breakthrough Health, Inc. <span className="font-semibold italic">If not us, then who?</span>
              </p>
              <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                For many of those we serve, life rarely offers a win. A 12-week cure is more than a medical milestone; it is proof that progress is possible and that something finally went right. That single success can spark the confidence to take the next step toward recovery, housing, or employment, creating momentum that turns one victory into many. This is not just a platform. It is a call to redesign care, not around institutions, but around people.
              </p>
              <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                We are clinicians who have seen the gap up close. We are operators who know how to scale solutions. And we are partners looking to rebuild the broken bridge between medical possibility and public health reality.
              </p>
              <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                We believe technology, when grounded in compassion and community, can do more than automate tasks, it can extend reach. It can find those slipping through the cracks. It can transform detection into cure. But only if we build it with the right values, for the right reasons, and in the right places.
              </p>
              <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                This platform is just a start. A plan to turn clinical truth into public health transformation. A model not of what might be, but of what must be.
              </p>
              <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-6 font-semibold">
                Because when the cure already exists, failure is no longer a mystery. It's a choice.
              </p>
              <p className="text-slate-700 dark:text-slate-300 leading-relaxed font-semibold">
                And we choose to fix it.
              </p>
              <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
                <p className="text-slate-600 dark:text-slate-400 font-medium">
                  Saumil J Patel, PharmD<br />
                  Pooja Amin, PharmD
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Treatment Journey */}
        <div id="treatment-journey" className="mt-20 max-w-6xl mx-auto">
          <h2 className="mb-8 text-3xl font-bold text-center text-slate-900 dark:text-white animate-fade-in">The Treatment Journey</h2>
          <div className="grid gap-6 md:grid-cols-4 stagger-animation">
            {[
              {
                icon: Stethoscope,
                title: 'Diagnosis',
                description: 'Blood tests to detect Hepatitis C antibodies and viral load',
              },
              {
                icon: ClipboardCheck,
                title: 'Assessment',
                description: 'Evaluation of liver damage and treatment planning',
              },
              {
                icon: Calendar,
                title: 'Treatment',
                description: '8-12 weeks of direct-acting antiviral medications',
              },
              {
                icon: CheckCircle2,
                title: 'Cure',
                description: 'Over 95% cure rate with modern treatments',
              },
            ].map((step, idx) => (
              <div
                key={step.title}
                className="relative rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 hover:border-blue-300 dark:hover:border-blue-700"
              >
                <div className="absolute -top-3 -left-3 flex size-8 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-teal-600 text-white font-bold text-sm">
                  {idx + 1}
                </div>
                <div className="mb-4 inline-flex size-12 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
                  <step.icon className="size-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">{step.title}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* About Platform */}
        <div id="our-platform" className="mt-20 max-w-6xl mx-auto">
          <div className="rounded-2xl border border-blue-200 dark:border-blue-800 bg-gradient-to-br from-blue-50 via-white to-teal-50 dark:from-blue-950/30 dark:via-slate-900 dark:to-teal-950/30 p-8 md:p-12 animate-slide-in-up shadow-xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex size-12 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-teal-600">
                <BarChart3 className="size-6 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white">Our Platform</h2>
            </div>
            <div className="prose prose-slate dark:prose-invert max-w-none">
              <p className="text-lg text-slate-700 dark:text-slate-300 leading-relaxed">
                Breakthrough Health, Inc. is a comprehensive treatment management platform designed for healthcare
                providers treating Hepatitis C patients. Our system helps clinicians track patient progress,
                manage treatment protocols, and monitor outcomes throughout the entire care journey.
              </p>
            </div>
            <div className="mt-8 grid gap-4 md:grid-cols-3 stagger-animation">
              {[
                {
                  icon: ClipboardCheck,
                  title: 'Patient Management',
                  description: 'Complete patient records and treatment tracking',
                },
                {
                  icon: Calendar,
                  title: 'Treatment Plans',
                  description: 'Customizable protocols and appointment scheduling',
                },
                {
                  icon: BarChart3,
                  title: 'Outcome Monitoring',
                  description: 'Real-time tracking of viral loads and treatment success',
                },
              ].map((feature) => (
                <div
                  key={feature.title}
                  className="rounded-lg border border-blue-200 dark:border-blue-800 bg-white/60 dark:bg-slate-900/60 p-4 transition-all duration-300 hover:bg-white dark:hover:bg-slate-900 hover:shadow-md hover:-translate-y-1"
                >
                  <feature.icon className="size-8 text-blue-600 dark:text-blue-400 mb-3" />
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">{feature.title}</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Access Section */}
        <div id="access-platform" className="mt-20 max-w-4xl mx-auto">
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8 md:p-12 text-center animate-scale-in shadow-xl">
            <div className="flex justify-center mb-6">
              <div className="flex size-16 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-teal-600">
                <Shield className="size-8 text-white" />
              </div>
            </div>
            <h2 className="mb-4 text-3xl font-bold text-slate-900 dark:text-white">Healthcare Provider Access</h2>
            <p className="mb-8 text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
              This platform is restricted to authorized healthcare providers. If you are a clinician
              treating Hepatitis C patients, please enter your access passcode to continue.
            </p>
            <Button
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white shadow-lg hover:shadow-xl transition-all animate-pulse-glow"
              onClick={handleAccessClick}
            >
              Access Platform
            </Button>
            <p className="mt-6 text-sm text-slate-500 dark:text-slate-400">
              Protected access ensures patient data privacy and HIPAA compliance
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 py-8 mt-20">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center gap-4 md:flex-row md:justify-between">
            <div className="flex items-center gap-2">
              <img
                src={logoImage}
                alt="Breakthrough Health Logo"
                className="h-8 w-auto rounded"
              />
              <span className="text-sm text-slate-600 dark:text-slate-400">
                © {new Date().getFullYear()} Breakthrough Health, Inc. All rights reserved.
              </span>
            </div>
            <nav className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
              <a href="mailto:info@breakthroughhealth.app" className="text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">
                Contact
              </a>
              <a href="#" className="text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">
                Terms of Service
              </a>
            </nav>
          </div>
        </div>
      </footer>

      {/* Passcode Dialog */}
      <PasscodeDialog
        open={passcodeDialogOpen}
        onOpenChange={setPasscodeDialogOpen}
        onSuccess={handlePasscodeSuccess}
      />

      {/* Login Dialog */}
      <LoginDialog
        open={loginDialogOpen}
        onOpenChange={setLoginDialogOpen}
        onSuccess={handleLoginSuccess}
      />

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes gradient {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        @keyframes slide-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 0 20px rgba(59, 130, 246, 0.3);
          }
          50% {
            box-shadow: 0 0 40px rgba(59, 130, 246, 0.6);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }

        .animate-gradient {
          background-size: 200% auto;
          animation: gradient 3s linear infinite;
        }

        .animate-slide-in-up {
          animation: slide-in-up 0.8s ease-out;
        }

        .animate-scale-in {
          animation: scale-in 0.6s ease-out;
        }

        .animate-float {
          animation: float 3s ease-in-out infinite;
        }

        .animate-pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }

        /* Staggered animations for children */
        .stagger-animation > * {
          opacity: 0;
          animation: slide-in-up 0.6s ease-out forwards;
        }

        .stagger-animation > *:nth-child(1) { animation-delay: 0.1s; }
        .stagger-animation > *:nth-child(2) { animation-delay: 0.2s; }
        .stagger-animation > *:nth-child(3) { animation-delay: 0.3s; }
        .stagger-animation > *:nth-child(4) { animation-delay: 0.4s; }
        .stagger-animation > *:nth-child(5) { animation-delay: 0.5s; }
        .stagger-animation > *:nth-child(6) { animation-delay: 0.6s; }
      `}</style>
    </div>
  )
}
